import React from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Grid,
  Divider,
} from "@mui/material";
import { colors } from "../..";

export const AppointmentDetail = ({ data }) => {
  const products = data?.products || [];
  const notes = data?.service_notes || "";
  const recipeName = data?.recipe_name || null;

  if (!products.length) {
    return (
      <Box sx={{ px: 1, py: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No formula/products recorded for this appointment.
        </Typography>
      </Box>
    );
  }

  const handlePrint = () => {
    const printContent = document.getElementById(
      `formula-card-${data.schedule_entry_id}`
    );
    if (!printContent) return;

    const printWindow = window.open("", "", "width=800,height=600");
    printWindow.document.write(`
      <html>
        <head>
          <title>Formula Card - ${data.client_name || ""}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; margin: 0; }
            h2 { color: ${colors.black}; border-bottom: 2px solid ${colors.gold}; padding-bottom: 10px; }
            table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            th, td { border: 1px solid #ddd; padding: 12px; text-align: left; }
            th { background-color: ${colors.black}; color: #fff; font-weight: bold; }
            .section { margin: 20px 0; padding: 15px; background-color: #fafafa; border-radius: 4px; }
            .section-title { font-weight: bold; font-size: 14px; margin-bottom: 10px; color: #555; }
            @media print { body { padding: 10px; } }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
          <script>
            window.onload = function() { window.print(); window.close(); }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <Box>
      <div
        id={`formula-card-${data.schedule_entry_id}`}
        style={{ display: "none" }}
      >
        <h2>Formula Card</h2>
        <p>
          <strong>Client:</strong> {data.client_name}
        </p>
        <p>
          <strong>Date:</strong>{" "}
          {data.appointment_date
            ? new Date(data.appointment_date).toLocaleDateString()
            : "—"}
        </p>
        <p>
          <strong>Stylist:</strong> {data.stylist_name}
        </p>
        {recipeName && (
          <p>
            <strong>Recipe:</strong> {recipeName}
          </p>
        )}
        <table>
          <thead>
            <tr>
              <th>Product</th>
              <th>Brand</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            {products.map((product, index) => (
              <tr key={product.id || index}>
                <td>{product.product_name || "N/A"}</td>
                <td>{product.brand || "N/A"}</td>
                <td>
                  {product.amount_used} {product.unit || ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {notes && (
          <div className="section">
            <div className="section-title">Service Notes:</div>
            <p>{notes}</p>
          </div>
        )}
      </div>

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Typography
            variant="body2"
            fontWeight={700}
            sx={{ mb: 1.25, color: "text.primary" }}
          >
            Formula Recipe
            {recipeName ? ` — ${recipeName}` : ""}
          </Typography>
          <TableContainer
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: "6px",
              bgcolor: "background.paper",
              overflow: "hidden",
            }}
          >
            <Table size="small">
              <TableHead
                sx={{
                  bgcolor: "secondary.main",
                  borderBottom: "1px solid",
                  borderColor: "primary.main",
                  "& .MuiTableCell-head": {
                    color: "common.white",
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    py: 1,
                  },
                }}
              >
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell>Brand</TableCell>
                  <TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {products.map((product, index) => (
                  <TableRow
                    key={product.id || index}
                    sx={{
                      "&:nth-of-type(odd)": {
                        bgcolor: (theme) =>
                          theme.palette.mode === "dark" ? "#1b1b1e" : "#fafafa",
                      },
                      "&:nth-of-type(even)": {
                        bgcolor: (theme) =>
                          theme.palette.mode === "dark" ? "#141416" : "transparent",
                      },
                      "& td": { py: 1, color: "text.primary", borderColor: "divider" },
                    }}
                  >
                    <TableCell>{product.product_name || "N/A"}</TableCell>
                    <TableCell>{product.brand || "N/A"}</TableCell>
                    <TableCell align="right">
                      <strong>{product.amount_used}</strong> {product.unit || ""}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
            Total Products: {products.length}
          </Typography>
        </Grid>

        <Grid item xs={12} md={4}>
          {notes && (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: "6px",
              }}
            >
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Service Notes
              </Typography>
              <Divider sx={{ mb: 1 }} />
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {notes}
              </Typography>
            </Paper>
          )}

          <Paper
            elevation={0}
            sx={{
              p: 2,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: "6px",
            }}
          >
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Actions
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handlePrint}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                boxShadow: "none",
                "&:hover": { boxShadow: "none" },
              }}
            >
              Print Formula
            </Button>
          </Paper>

          <Box
            sx={{
              mt: 2,
              p: 2,
              borderRadius: "6px",
              bgcolor: (theme) =>
                theme.palette.mode === "dark" ? "#1b1b1e" : "#f2f2f2",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography variant="caption" display="block" gutterBottom>
              <strong>Client:</strong> {data.client_name}
            </Typography>
            <Typography variant="caption" display="block" gutterBottom>
              <strong>Date:</strong>{" "}
              {data.appointment_date
                ? new Date(data.appointment_date).toLocaleDateString()
                : "—"}
            </Typography>
            <Typography variant="caption" display="block">
              <strong>Stylist:</strong> {data.stylist_name}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};
